// ---------------------------------------------------------------------------
// PaneErrorBanner — bloco de erro embutido (NAO toast).
// ---------------------------------------------------------------------------
// Padrao da Frente Resiliencia da V3:
//
//   - Falha em carregar dados de uma aba do drawer ("Este mes",
//     "Cruzamentos", "Sobre este input") -> banner inline, com
//     opcao de "Tentar novamente". O usuario nao perde contexto e a
//     mensagem nao desaparece.
//
//   - Falha em SALVAR (responsaveis, limites, instrucoes) -> toast
//     transitorio via ``notify(...)``. Erro de gravacao precisa de
//     atencao mas nao tranca a UI.
//
// Este arquivo e a peca "banner". Toasts seguem usando ``notify``.
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import Refresh from "@material-ui/icons/Refresh";

import {
  dangerColor,
  grayColor,
  whiteColor,
} from "../../../../../assets/jss/material-dashboard-pro-react";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    padding: "14px 16px",
    margin: "0 0 16px 0",
    borderLeft: `3px solid ${dangerColor[0]}`,
    background: "#fdecea",
    borderRadius: "4px",
  },
  icon: { color: dangerColor[0], marginTop: "2px" },
  body: { flex: 1, minWidth: 0 },
  title: {
    fontWeight: 600,
    fontSize: "13px",
    color: grayColor[2],
    marginBottom: "4px",
  },
  message: { fontSize: "12px", color: grayColor[1], lineHeight: 1.45 },
  detail: {
    marginTop: "6px",
    fontSize: "11px",
    color: grayColor[0],
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
    background: "#fff",
    border: `1px solid ${grayColor[8]}`,
    borderRadius: "3px",
    padding: "4px 6px",
    overflowWrap: "anywhere",
  },
  actions: { marginTop: "10px", display: "flex", gap: "8px" },
  retryBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    fontWeight: 600,
    color: dangerColor[0],
    background: whiteColor,
    border: `1px solid ${dangerColor[0]}`,
    borderRadius: "4px",
    padding: "5px 10px",
    cursor: "pointer",
    "&:hover": { background: "#fff5f5" },
    "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
  },
}));

/**
 * @param {object} props
 * @param {string} props.title  Frase em linguagem de negocio (1 linha).
 * @param {string} [props.message]  Detalhamento opcional (1-2 linhas).
 * @param {string} [props.detail]  Codigo/payload tecnico opcional.
 * @param {() => void} [props.onRetry]  Quando passado, exibe botao
 *   "Tentar novamente". Use para acoes idempotentes (re-fetch).
 * @param {boolean} [props.retrying]  Desabilita botao + exibe label.
 */
const PaneErrorBanner = ({ title, message, detail, onRetry, retrying }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <div className={classes.root} role="alert">
      <ErrorOutline className={classes.icon} fontSize="small" />
      <div className={classes.body}>
        <div className={classes.title}>{title}</div>
        {message ? <div className={classes.message}>{message}</div> : null}
        {detail ? <div className={classes.detail}>{detail}</div> : null}
        {onRetry ? (
          <div className={classes.actions}>
            <button
              type="button"
              className={classes.retryBtn}
              onClick={onRetry}
              disabled={!!retrying}
            >
              <Refresh fontSize="small" />
              {retrying
                ? t("modeling_inputs_v3.pane_error.retrying")
                : t("modeling_inputs_v3.pane_error.retry_btn")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

PaneErrorBanner.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string,
  detail: PropTypes.string,
  onRetry: PropTypes.func,
  retrying: PropTypes.bool,
};

export default PaneErrorBanner;
